import {
  ApplyCouponRequest,
  ArchiveDraftRequest,
  CreateDraftJobForTeamRequest,
  CreateDraftJobRequest,
  GetJobSubmissionProgressRequest,
  GetQuoteRequest,
  RemoveCouponRequest,
  ReplaceDraftQuestionAnswersRequest,
  RequestChampionProviderRequest,
  SubmitDraftJobRequest,
  UpgradeLegendRequest,
} from '@taxfyle/api-internal/internal/draft_pb'
import { StringValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import { delay } from 'fejl'
import { Id } from '@/infra/id'
import { onRpcNotFoundReturnNull, retryOnRpcError } from '@/utils/grpc-util'
import type { FetchBillingTransactionsFn } from '@/features/billing'
import type { JobsApi } from '@/features/jobs/api/job-api'
import type { Job } from '@/features/jobs/Job'
import type { JobStore } from '@/features/jobs/JobStore'
import type { LegendVersionId } from '@/features/jobs/legends/PublishedLegend'
import type { AnswersMap } from '@/features/jobs/questions/questions'
import { populateAnswerMapProto } from '@/features/jobs/questions/question-mappers'
import type { Quote } from './Quote'
import { mapGetQuoteResponseToQuote } from './draft-job-mappers'

/**
 * Store for draft jobs.
 */
export class DraftJobStore {
  constructor(
    private readonly _api: JobsApi,
    private readonly _jobStore: JobStore,
    private readonly _fetchBillingTransactions: FetchBillingTransactionsFn,
  ) {}

  /**
   * Fetches a quote for the job.
   *
   * @param id
   * @param signal
   */
  async fetchQuote(id: Id, signal?: AbortSignal): Promise<Quote> {
    const request = new GetQuoteRequest().setId(Id.assert(id))
    const response = await retryOnRpcError(
      () => this._api.drafts.getQuote(request),
      { signal },
    )
    signal?.throwIfAborted()

    return mapGetQuoteResponseToQuote(response)
  }

  /**
   * Creates a draft job.
   */
  async createDraftJob(
    legendVersionId: LegendVersionId,
    clientUserPublicId: Id,
  ): Promise<Job> {
    const request = new CreateDraftJobRequest()
      .setLegendId(legendVersionId.id)
      .setLegendVersion(legendVersionId.version)
      .setClientUserPublicId(Id.assert(clientUserPublicId))

    const draft = await this._api.drafts.createDraftJob(request)
    return this._refetchJob(draft.getId())
  }

  /**
   * Creates a draft job for a team.
   */
  async createTeamDraftJob({
    legendVersionId,
    teamId,
    clientUserPublicId,
  }: CreateTeamDraftJobOptions): Promise<Job> {
    const request = new CreateDraftJobForTeamRequest()
      .setLegendId(legendVersionId.id)
      .setLegendVersion(legendVersionId.version)
      .setClientUserPublicId(Id.assert(clientUserPublicId))
      .setTeamId(Id.assert(teamId))

    const draft = await this._api.drafts.createDraftJobForTeam(request)
    return this._refetchJob(draft.getId())
  }

  /**
   * Upgrades the draft job to the latest version of its legend.
   *
   * @param jobId
   */
  async upgradeLegendVersion(jobId: Id) {
    const request = new UpgradeLegendRequest().setId(Id.assert(jobId))
    const draft = await this._api.drafts.upgradeLegend(request)
    return this._refetchJob(draft.getId())
  }

  /**
   * Replaces the answers with the ones specified.
   *
   * @param jobId
   * @param answers
   */
  async replaceQuestionAnswers(jobId: Id, answers: AnswersMap) {
    const request = new ReplaceDraftQuestionAnswersRequest().setId(
      Id.assert(jobId),
    )
    populateAnswerMapProto(answers, request.getAnswersMap())

    await this._api.drafts.replaceDraftQuestionAnswers(request)
  }

  /**
   * Requests a champion provider for a draft.
   *
   * @param jobId
   * @param championProviderId When null, any champion provider that can be
   * assigned to this job will be requested.
   */
  async requestChampionProvider(jobId: Id, championProviderId: Id | null) {
    const request = new RequestChampionProviderRequest().setId(Id.assert(jobId))

    if (championProviderId) {
      request.setProviderUserPublicId(
        new StringValue().setValue(Id.assert(championProviderId)),
      )
    }

    await this._api.drafts.requestChampionProvider(request)

    await this._refetchJob(jobId)
  }

  /**
   * Applies a coupon to a draft.
   *
   * @param jobId
   * @param couponId
   */
  async applyCoupon(jobId: Id, couponId: Id) {
    const request = new ApplyCouponRequest()
      .setId(Id.assert(jobId))
      .setCouponId(new StringValue().setValue(Id.assert(couponId)))

    await this._api.drafts.applyCoupon(request)

    await this._refetchJob(jobId)
  }

  /**
   * Removes the coupon from a draft.
   *
   * @param jobId
   */
  async removeCoupon(jobId: Id) {
    const request = new RemoveCouponRequest().setId(Id.assert(jobId))

    await this._api.drafts.removeCoupon(request)

    await this._refetchJob(jobId)
  }

  /**
   * Archives a draft as one of its collaborators.
   *
   * @param id
   */
  async archive(id: Id) {
    id = Id.assert(id)

    await this._api.drafts.archive(new ArchiveDraftRequest().setId(id))

    this._jobStore.jobs.remove(id)
  }

  /**
   * Submits the draft and returns a submission ID that can be passed
   * to {@link waitForSubmissionOutcome}.
   */
  async submit({
    id,
    billingMethodId,
    bypassBilling,
  }: SubmitOptions): Promise<Id> {
    const request = new SubmitDraftJobRequest().setId(Id.assert(id))

    if (bypassBilling) {
      request.setBypassBilling(bypassBilling)
    }

    if (billingMethodId) {
      request.setBillingMethodId(
        new StringValue().setValue(Id.assert(billingMethodId)),
      )
    }

    const response = await this._api.drafts.submitDraftJob(request)
    return response.getSubmissionId()
  }

  /**
   * Waits for a job submission to finish and returns the submitted job.
   * A failed submission is thrown as an {@link Error}.
   */
  async waitForSubmissionOutcome({
    submissionId,
    signal,
  }: WaitForSubmissionOutcomeOptions): Promise<Job> {
    const request = new GetJobSubmissionProgressRequest().setSubmissionId(
      Id.assert(submissionId),
    )

    while (true) {
      signal?.throwIfAborted()

      const response = await this._api.drafts
        .getJobSubmissionProgress(request)
        .catch(onRpcNotFoundReturnNull)
      if (response === null) {
        // The submission may not be readable yet right after submitting.
        await delay(400)
        continue
      }

      const status = response.getStatus()
      if (status?.getCompleted()) {
        const job = await this._refetchJob(response.getId())
        await this._fetchBillingTransactions(job.stripeCharges)
        return job
      }

      const failed = status?.getFailed()
      if (failed) {
        throw new Error(failed.getDescription())
      }

      if (status?.getInProgress()) {
        await delay(400)
        continue
      }

      console.error(
        'Unexpected status from submission progress endpoint',
        response.toObject(),
      )
      throw new Error('Unexpected status from submission progress endpoint')
    }
  }

  private _refetchJob(id: Id) {
    return this._jobStore.fetchJob(id, { force: true })
  }
}

/**
 * Options for creating a draft job for a team.
 */
export interface CreateTeamDraftJobOptions {
  legendVersionId: LegendVersionId
  teamId: Id
  clientUserPublicId: Id
}

/**
 * Options for submitting a draft job.
 */
export interface SubmitOptions {
  /**
   * ID of the job to submit.
   */
  id: Id

  /**
   * ID of the billing method to use, if any.
   */
  billingMethodId?: Id | null

  /**
   * Whether to submit without billing the client.
   */
  bypassBilling?: boolean
}

/**
 * Options for waiting for a job submission to finish.
 */
export interface WaitForSubmissionOutcomeOptions {
  submissionId: Id

  /**
   * Checked before each poll request.
   */
  signal?: AbortSignal
}
